import { Add, AutoFixHigh, Delete, Save, Settings } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DynamicLayout, PimoDynamicReactLayout } from "@pimo/pimo-app-builder";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { useMemo, useState } from "react";

import PMPopUp from "../../pm-components/pm-pop-up/pm-pop-up";
import { ComponentEditor } from "./component-editor";
import { useEditorHighlightColor } from "./get-editor-highlight-color";
import { Library } from "./library";
import { Row } from "./row";
import {
  DragDropLayoutComponentLayoutProps,
  DragDropLayoutEditSchema,
  DragDropLayoutEventNames,
  DragDropLayoutEventPayload,
  DragDropLayoutRowDefinition,
  DragDropLayoutState,
  DragDropLayoutTabDefinition,
} from "./types";

export class DragAndDropLayout extends DynamicLayout<
  DragDropLayoutState,
  DragDropLayoutEventNames,
  DragDropLayoutEventPayload,
  DragDropLayoutComponentLayoutProps
> {
  getLayoutComponent(): PimoDynamicReactLayout<
    DragDropLayoutState,
    DragDropLayoutEventNames,
    DragDropLayoutEventPayload,
    DragDropLayoutComponentLayoutProps
  > {
    return DragAndDropReactLayout;
  }
}

const defaultRow: DragDropLayoutRowDefinition = {
  id: 0,
  columnCount: 1,
  columns: [],
};

const defaultTab: DragDropLayoutTabDefinition = {
  id: 0,
  title: "Tab 1",
  rows: [defaultRow],
};

export const DragAndDropReactLayout: PimoDynamicReactLayout<
  DragDropLayoutState,
  DragDropLayoutEventNames,
  DragDropLayoutEventPayload,
  DragDropLayoutComponentLayoutProps
> = ({
  components,
  tabs: inputTabs = [],
  displayTabs = false,
  activeTabIndex: controlledTabIndex,
  onTabChange,
  fireEvent,
  canEdit,
  rightSlot,
  sidePanel,
  isAiEnabled,
  isComponentEditingEnabled,
  toolbarButtons,
  dropdownMenus,
  language = "en",
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const isHeaderNarrow = useMediaQuery(theme.breakpoints.down("lg"));

  const [isEditing, setIsEditing] = useState(false);
  const [tabs, setTabs] = useState<DragDropLayoutTabDefinition[]>(
    inputTabs.length > 0 ? inputTabs : [defaultTab]
  );

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(
    controlledTabIndex ?? 0
  );

  // Sync with controlled activeTabIndex when it changes using the
  // "store previous prop in state" pattern instead of a useEffect.
  const [previousControlledTabIndex, setPreviousControlledTabIndex] =
    useState(controlledTabIndex);
  if (controlledTabIndex !== previousControlledTabIndex) {
    setPreviousControlledTabIndex(controlledTabIndex);
    if (controlledTabIndex !== undefined && controlledTabIndex >= 0) {
      setSelectedTabIndex(controlledTabIndex);
    }
  }

  // Sync incoming tabs prop with local state (only when not editing)
  // Uses deep equality to handle new array references from parent
  const [previousInputTabs, setPreviousInputTabs] = useState(inputTabs);
  const isTabsEqual = useMemo(() => {
    try {
      return JSON.stringify(inputTabs) === JSON.stringify(previousInputTabs);
    } catch {
      return inputTabs === previousInputTabs;
    }
  }, [inputTabs, previousInputTabs]);

  if (!isEditing && !isTabsEqual && inputTabs !== previousInputTabs) {
    setPreviousInputTabs(inputTabs);

    const nextTabs = inputTabs.length > 0 ? inputTabs : [defaultTab];

    let isDeeplyEqual = false;
    try {
      isDeeplyEqual = JSON.stringify(tabs) === JSON.stringify(nextTabs);
    } catch (err) {
      console.warn("Error comparing tabs for equality:", err);
    }

    // Only update previousInputTabs (and therefore call setState during render)
    // when the content actually changed. If the reference is new but the content
    // is identical — which happens when mapLayoutState recreates arrays each call
    // — skip the setState entirely to avoid an infinite re-render loop.
    if (!isDeeplyEqual) {
      setPreviousInputTabs(inputTabs);
      setTabs(nextTabs);
      setSelectedTabIndex((prev) =>
        prev < nextTabs.length ? prev : nextTabs.length - 1
      );
    }
  }

  // Create a stable key that reflects the tabs content (including rows/columns)
  // This forces a remount of the main content when tabs change deeply.
  const tabsKey = useMemo(() => {
    return tabs
      .map(
        (t) =>
          `${t.id}::${t.rows
            .map((r) => `${r.id}-${r.columnCount}-${JSON.stringify(r.columns)}`)
            .join("|")}`
      )
      .join("||");
  }, [tabs]);

  const handleTabChange = (newIndex: number) => {
    setSelectedTabIndex(newIndex);
    onTabChange?.(newIndex);
  };
  // Safely pick the active tab: first try tabs[selectedTabIndex], then tabs[0]
  const activeTab: DragDropLayoutTabDefinition =
    tabs[selectedTabIndex] || tabs[0];
  const activeRows: DragDropLayoutRowDefinition[] = activeTab.rows;

  const updateActiveTabRows = (newRows: DragDropLayoutRowDefinition[]) => {
    setTabs((prev) =>
      prev.map((t, idx) =>
        idx === selectedTabIndex
          ? {
              ...t,
              rows: newRows,
            }
          : t
      )
    );
  };

  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingComponentId, setEditingComponentId] = useState<string | null>(
    null
  );
  const [editingComponentSchema, setEditingComponentSchema] =
    useState<DragDropLayoutEditSchema | null>(null);

  const [pendingDeleteTab, setPendingDeleteTab] = useState<number | null>(null);
  const [isDraggingTab, setIsDraggingTab] = useState(false);
  const [draggedTabIndex, setDraggedTabIndex] = useState<number | null>(null);
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);

  const deletingTabTitle =
    pendingDeleteTab !== null && tabs[pendingDeleteTab]
      ? tabs[pendingDeleteTab].title
      : "";

  const [openCloseWithoutSavingDialog, setOpenCloseWithoutSavingDialog] =
    useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleAddNewTab = () => {
    const newId = tabs.length > 0 ? Math.max(...tabs.map((t) => t.id)) + 1 : 0;
    const newTab: DragDropLayoutTabDefinition = {
      id: newId,
      title: `Tab ${tabs.length + 1}`,
      rows: [defaultRow],
    };
    setTabs((prev) => [...prev, newTab]);
    setSelectedTabIndex(tabs.length);
    setHasChanges(true);
  };

  const deleteTab = (idx: number) => {
    setTabs((prev) => {
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
    if (idx < selectedTabIndex) {
      setSelectedTabIndex((sel) => sel - 1);
    } else if (idx === selectedTabIndex) {
      setSelectedTabIndex((prevSel) => (prevSel === 0 ? 0 : prevSel - 1));
    }
    setHasChanges(true);
  };

  const handleRenameTab = (idx: number, newTitle: string) => {
    setTabs((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, title: newTitle } : t))
    );
    setHasChanges(true);
  };

  const moveRow = (rowId: number, direction: "up" | "down") => {
    const rowIndex = activeRows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1) {
      return;
    }
    const newRows = [...activeRows];
    const [movedRow] = newRows.splice(rowIndex, 1);

    if (direction === "up") {
      newRows.splice(rowIndex - 1, 0, movedRow);
    } else {
      newRows.splice(rowIndex + 1, 0, movedRow);
    }
    updateActiveTabRows(newRows);
    setHasChanges(true);
  };

  const handleMoveRowUp = (rowId: number) => {
    moveRow(rowId, "up");
  };
  const handleMoveRowDown = (rowId: number) => {
    moveRow(rowId, "down");
  };

  const handleAddRowBelow = (rowId: number, numberOfColumns: number) => {
    const rowIndex = activeRows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1) {
      return;
    }
    const newRows = [...activeRows];
    const newRow: DragDropLayoutRowDefinition = {
      id: Math.max(...activeRows.map((r) => r.id)) + 1,
      columnCount: numberOfColumns,
      columns: [],
    };
    newRows.splice(rowIndex + 1, 0, newRow);
    updateActiveTabRows(newRows);
    setHasChanges(true);
  };

  const handleDeleteRow = (rowId: number) => {
    const rowIndex = activeRows.findIndex((row) => row.id === rowId);
    if (rowIndex === -1) {
      return;
    }
    const newRows = [...activeRows];
    newRows.splice(rowIndex, 1);
    if (newRows.length === 0) {
      newRows.push(defaultRow);
    }
    updateActiveTabRows(newRows);
    setHasChanges(true);
  };

  const handleComponentDropped = ({
    rowId,
    columnIndex,
    componentId,
  }: {
    rowId: number;
    columnIndex: number;
    componentId: string;
  }) => {
    const tabsWithoutThisComponent = tabs.map((t) => ({
      ...t,
      rows: t.rows.map((r) => ({
        ...r,
        columns: r.columns.filter((c) => c.componentId !== componentId),
      })),
    }));

    const updatedActiveTab = tabsWithoutThisComponent[selectedTabIndex];
    if (!updatedActiveTab) return;

    const newRows = updatedActiveTab.rows.map((r) => {
      if (r.id !== rowId) return r;
      const existingIndex = r.columns.findIndex(
        (c) => c.columnIndex === columnIndex
      );
      if (existingIndex !== -1) {
        const newCols = [...r.columns];
        newCols[existingIndex] = { columnIndex, componentId };
        return { ...r, columns: newCols };
      } else {
        return {
          ...r,
          columns: [...r.columns, { columnIndex, componentId }],
        };
      }
    });

    setTabs((prev) =>
      prev.map((t, idx) =>
        idx === selectedTabIndex ? { ...t, rows: newRows } : { ...t }
      )
    );
    setHasChanges(true);
  };

  const handleDeleteComponent = (componentId: string) => {
    const newRows = activeRows.map((r) => ({
      ...r,
      columns: r.columns.filter((c) => c.componentId !== componentId),
    }));
    updateActiveTabRows(newRows);
    setHasChanges(true);
  };

  const handleEditComponent = (componentId: string) => {
    const component = components.find(
      (c) => c.layoutProps?.componentId === componentId
    );
    if (!component) return;
    const editSchema = component.layoutProps?.editSchema;
    if (!editSchema) {
      console.warn(
        `Component with id ${componentId} does not have an edit schema.`
      );
      return;
    }
    setEditingComponentId(componentId);
    setEditingComponentSchema(editSchema);
    setHasChanges(true);
  };

  const handleAiGeneration = async () => {
    const purpose = prompt("What is the purpose of the page?");
    if (!purpose) {
      return;
    }

    setIsLoading(true);
    const availableComponents = components.map((c) => c.layoutProps);
    const payload = { availableComponents, purpose };
    const response = await fetch(
      `https://n8n.cloud.hipsquare.io/webhook/23d1cd3a-466d-4bd1-88c0-a8cd19acfdd7`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );
    const body = await response.json();
    const generatedRows: DragDropLayoutRowDefinition[] = body.output;
    updateActiveTabRows(generatedRows);
    setIsLoading(false);
    setHasChanges(true);
  };

  const componentsInLibrary = useMemo(() => {
    const usedIds = new Set<string>(
      activeRows.flatMap((r) => r.columns.map((c) => c.componentId))
    );
    return components
      .filter((c) => c.layoutProps?.componentId)
      .filter((c) => {
        const id = c.layoutProps?.componentId ?? "";
        return !usedIds.has(id);
      });
  }, [components, activeRows]);

  const globalComponents = useMemo(
    () => components.filter((c) => !c.layoutProps?.componentId),
    [components]
  );

  const handleToggleEditing = () => {
    if (isEditing) {
      fireEvent?.("updateLayout", {
        tabs,
      });
      fireEvent?.("stopLayoutEdit");
    } else {
      fireEvent?.("startLayoutEdit");
    }
    setIsEditing((prev) => !prev);
  };

  const editorHighlightColor = useEditorHighlightColor();

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </div>
      )}

      <div
        data-pimo-dnd-container
        style={{
          display: "flex",
          flexDirection: isNarrow ? "column" : "row",
          gap: isNarrow ? "12px" : "25px",
          height: isEditing ? "calc(100vh - 96px)" : undefined,
          maxHeight: isEditing ? "calc(100vh - 96px)" : undefined,
          minHeight: 0,
          zIndex: 2,
        }}
      >
        <div
          data-pimo-dnd-background
          style={{
            flex: "1 1 100%",
            display: "flex",
            flexDirection: "column",
            maxWidth: isEditing ? "calc(100% - 350px - 25px)" : "100%",
            minHeight: 0,
            borderRadius: "12px",
            background: theme.palette.secondary.main,
          }}
        >
          {(canEdit || displayTabs || rightSlot) && (
            <div
              data-pimo-dnd-toolbar
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: displayTabs ? "space-between" : "flex-end",
                alignItems: "center",
                gap: "8px",
                padding: isNarrow ? "12px 12px 0 12px" : "16px 28px 0 30px",
                overflow: isHeaderNarrow ? "hidden" : "visible",
              }}
            >
              {/* View-mode tabs */}
              {displayTabs && !isEditing && (
                <Tabs
                  value={selectedTabIndex}
                  onChange={(_, newIndex) => handleTabChange(newIndex)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    px: 0,
                    borderBottom: `2px solid transparent`,
                    "& .MuiTabScrollButton-root:first-of-type": {
                      width: 0,
                      overflow: "hidden",
                    },
                  }}
                >
                  {tabs.map((tab, idx) => (
                    <Tab
                      key={tab.id}
                      label={tab.title}
                      value={idx}
                      sx={{
                        textTransform: "none",
                        fontWeight:
                          idx === selectedTabIndex ? "bold" : "normal",
                        fontSize: "14px",
                        padding: "8px 16px",
                        justifyContent: "center",
                        color: theme.palette.text.primary,
                      }}
                    />
                  ))}
                </Tabs>
              )}

              {/* Edit-mode tabs */}
              {displayTabs && isEditing && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    overflowX: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",

                      padding: isEditing ? "4px" : "0",
                      borderRadius: 0,
                    }}
                  >
                    {tabs.map((tab, idx) => {
                      const isActive = idx === selectedTabIndex;
                      return (
                        <div
                          key={tab.id}
                          onClick={() => handleTabChange(idx)}
                          style={{
                            padding: "8px 16px",
                            minWidth: "100px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            borderRadius: "6px 6px 0 0",
                            transition: "all 0.2s ease",
                            opacity:
                              isDraggingTab && draggedTabIndex === idx
                                ? 0.5
                                : 1,
                            transform:
                              isDraggingTab && draggedTabIndex === idx
                                ? "scale(1.05)"
                                : "none",
                            boxShadow:
                              hoveredTabIndex === idx && !isDraggingTab
                                ? `inset 4px 0 0 0 ${editorHighlightColor}`
                                : "none",
                          }}
                          draggable={isEditing}
                          onMouseDown={() => {
                            if (document.activeElement instanceof HTMLElement)
                              document.activeElement.blur();
                          }}
                          onDragStart={(e) => {
                            if (document.activeElement instanceof HTMLElement) {
                              document.activeElement.blur();
                            }
                            setDraggedTabIndex(idx);
                            setIsDraggingTab(true);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => {
                            setIsDraggingTab(false);
                            setDraggedTabIndex(null);
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            setHoveredTabIndex(idx);
                          }}
                          onDragLeave={() => setHoveredTabIndex(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (
                              draggedTabIndex !== null &&
                              draggedTabIndex !== idx
                            ) {
                              const newTabs = [...tabs];
                              const [moved] = newTabs.splice(
                                draggedTabIndex,
                                1
                              );
                              newTabs.splice(idx, 0, moved);
                              setTabs(newTabs);
                              setSelectedTabIndex(idx);
                            }
                            setDraggedTabIndex(null);
                            setHoveredTabIndex(null);
                          }}
                        >
                          {isEditing && isActive ? (
                            <input
                              style={{
                                fontSize: "15px",
                                border: `1px dashed ${editorHighlightColor}`,
                                borderRadius: "3px",
                                outline: "none",
                                padding: "2px 4px",
                                fontWeight: "bolder",
                              }}
                              value={tab.title}
                              onChange={(e) =>
                                handleRenameTab(idx, e.target.value)
                              }
                              onBlur={(e) => {
                                if (e.target.value.trim() === "") {
                                  handleRenameTab(idx, tab.title);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  (e.target as HTMLInputElement).blur();
                                }
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                fontWeight: isActive ? "bolder" : 500,
                                fontSize: "14px",
                                fontFamily: "inherit",
                                padding: "2px 4px",
                              }}
                            >
                              {tab.title}
                            </span>
                          )}
                          {canEdit && isEditing && tabs.length > 1 && (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingDeleteTab(idx);
                              }}
                              style={{
                                cursor: "pointer",
                                color: editorHighlightColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Delete
                                fontSize="medium"
                                style={{
                                  color: "#fff",
                                  backgroundColor: editorHighlightColor,
                                  borderRadius: "50%",
                                  padding: "4px",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {canEdit && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddNewTab();
                      }}
                      style={{
                        cursor: "pointer",
                        marginLeft: "8px",
                        height: "28px",
                        width: "28px",
                        borderRadius: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Add
                        style={{
                          color: editorHighlightColor,
                          border: `${editorHighlightColor} 2px solid`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
              {(canEdit || rightSlot || toolbarButtons || dropdownMenus) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    flexShrink: 0,
                    justifyContent: "flex-end",
                    overflowX: "auto",
                    maxWidth: "100%",
                  }}
                >
                  {toolbarButtons?.map((button, index) => (
                    <Button
                      key={index}
                      startIcon={button.icon}
                      onClick={button.onClick}
                      variant={button.variant ?? "outlined"}
                      color={button.color}
                      size="small"
                      sx={button.sx}
                    >
                      {button.text}
                    </Button>
                  ))}

                  {dropdownMenus?.map((menu, index) => (
                    <Button
                      key={index}
                      startIcon={menu.icon}
                      onClick={(e) => {
                        const dropdown = document.createElement("div");
                        dropdown.style.position = "fixed";
                        dropdown.style.backgroundColor = "white";
                        dropdown.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
                        dropdown.style.borderRadius = "4px";
                        dropdown.style.zIndex = "9999";
                        dropdown.style.minWidth = "200px";

                        const rect = (
                          e.currentTarget as HTMLElement
                        ).getBoundingClientRect();
                        dropdown.style.top = `${rect.bottom + 4}px`;
                        dropdown.style.left = `${rect.left}px`;

                        menu.items.forEach((item) => {
                          const menuItem = document.createElement("div");
                          menuItem.style.padding = "8px 16px";
                          menuItem.style.cursor = "pointer";
                          menuItem.style.display = "flex";
                          menuItem.style.alignItems = "center";
                          menuItem.style.gap = "8px";
                          menuItem.textContent = item.dropdownTitle;
                          menuItem.onmouseenter = () => {
                            menuItem.style.backgroundColor = "#f5f5f5";
                          };
                          menuItem.onmouseleave = () => {
                            menuItem.style.backgroundColor = "white";
                          };
                          menuItem.onclick = () => {
                            item.dropdownOnClick();
                            document.body.removeChild(dropdown);
                          };
                          dropdown.appendChild(menuItem);
                        });

                        const closeDropdown = (event: MouseEvent) => {
                          if (!dropdown.contains(event.target as Node)) {
                            document.body.removeChild(dropdown);
                            document.removeEventListener(
                              "click",
                              closeDropdown
                            );
                          }
                        };

                        document.body.appendChild(dropdown);
                        setTimeout(
                          () =>
                            document.addEventListener("click", closeDropdown),
                          0
                        );
                      }}
                      variant="outlined"
                      size="small"
                    >
                      {menu.buttontitle}
                    </Button>
                  ))}

                  {canEdit && isEditing && isAiEnabled && (
                    <Button
                      startIcon={<AutoFixHigh />}
                      onClick={() => handleAiGeneration()}
                    >
                      AI-generate a layout
                    </Button>
                  )}

                  {rightSlot}
                  {canEdit && (
                    <Button
                      className="no-print"
                      startIcon={isEditing ? <Save /> : <Settings />}
                      onClick={() => handleToggleEditing()}
                      sx={{
                        padding: 0,
                        minWidth: "10px",

                        "&>.MuiButton-startIcon": {
                          margin: 0,
                        },
                      }}
                    ></Button>
                  )}
                </div>
              )}
            </div>
          )}

          <div
            data-pimo-dnd-stage
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: isEditing ? "auto" : undefined,
              boxSizing: "border-box",
            }}
          >
            <div key={tabsKey}>
              {activeRows.map((row) => (
                <Row
                  {...row}
                  key={row.id}
                  rowId={row.id}
                  components={components}
                  isEditing={!!isEditing}
                  isComponentEditingEnabled={isComponentEditingEnabled}
                  canDelete={activeRows.length > 1}
                  onHover={() => {
                    if (!isEditing) return;
                    setHoveredRowId(row.id);
                  }}
                  onLeave={() => {
                    setHoveredRowId(null);
                  }}
                  isHovered={hoveredRowId === row.id}
                  onAddRowBelowClicked={(numberOfColumns) => {
                    handleAddRowBelow(row.id, numberOfColumns);
                  }}
                  onComponentDropped={(componentId, _rowId, columnIndex) => {
                    handleComponentDropped({
                      rowId: row.id,
                      columnIndex,
                      componentId,
                    });
                  }}
                  onMoveRowUpClicked={(rowId) => {
                    handleMoveRowUp(rowId);
                  }}
                  onMoveRowDownClicked={(rowId) => {
                    handleMoveRowDown(rowId);
                  }}
                  onDeleteRowClicked={(rowId) => {
                    handleDeleteRow(rowId);
                  }}
                  onComponentDeleted={(componentId) => {
                    handleDeleteComponent(componentId);
                  }}
                  onEditComponent={(componentId) => {
                    handleEditComponent(componentId);
                  }}
                  onEditRowClicked={(rowId) => {
                    console.log(
                      "Editing a row is not currently supported:",
                      rowId
                    );
                  }}
                />
              ))}

              {/* Render global components */}
              {globalComponents.map((component, index) => {
                const RenderedComponent = component.render();
                return <RenderedComponent key={`global-${index}`} />;
              })}
            </div>
          </div>
        </div>

        {isEditing && (
          <div
            style={{
              background: "#fff",
              flex: isNarrow ? "1 1 auto" : "0 0 350px",
              maxWidth: isNarrow ? "100%" : "350px",
              minHeight: 0,
              padding: isNarrow ? "12px" : "15px",
              transition: "flex 0.5s, max-width 0.5s, padding 0.5s",
              overflow: "hidden",
              zIndex: 3,
            }}
          >
            <Library
              components={componentsInLibrary}
              language={language}
              onClose={() => {
                if (hasChanges) {
                  setOpenCloseWithoutSavingDialog(true);
                } else {
                  setIsEditing(false);
                  fireEvent?.("stopLayoutEdit");
                  setTabs(inputTabs.length > 0 ? inputTabs : [defaultTab]);
                }
              }}
            />
          </div>
        )}

        {sidePanel}
      </div>

      {editingComponentId && editingComponentSchema && (
        <ComponentEditor
          onClose={() => {
            setEditingComponentId(null);
            setEditingComponentSchema(null);
          }}
          editSchema={editingComponentSchema}
          component={components.find(
            (c) => c.layoutProps?.componentId === editingComponentId
          )}
          onChange={(value) => {
            fireEvent?.("componentEdited", {
              componentId: editingComponentId,
              componentValue: value,
            });
            setEditingComponentId(null);
            setEditingComponentSchema(null);
          }}
        />
      )}
      {/* Delete Tab Confirmation Dialog */}
      <Dialog
        open={pendingDeleteTab !== null}
        onClose={() => setPendingDeleteTab(null)}
      >
        <DialogTitle>
          {getLocalizedMessage(
            language,
            "dragDropLayout.deleteTabTitle"
          )?.replace("{{tabTitle}}", deletingTabTitle) ||
            `Delete "${deletingTabTitle}"?`}
        </DialogTitle>
        <DialogContent>
          {getLocalizedMessage(
            language,
            "dragDropLayout.deleteTabMessage"
          )?.replace("{{tabTitle}}", deletingTabTitle) ||
            `Are you sure you want to delete the tab "${deletingTabTitle}"? This action cannot be undone.`}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteTab(null)} color="primary">
            {getLocalizedMessage(language, "dragDropLayout.cancel") || "Cancel"}
          </Button>
          <Button
            onClick={() => {
              deleteTab(pendingDeleteTab!);
              setPendingDeleteTab(null);
            }}
            color="error"
          >
            {getLocalizedMessage(language, "dragDropLayout.delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <PMPopUp
        open={openCloseWithoutSavingDialog}
        title="Unsaved Changes"
        description="You have made changes that have not been saved. If you close the component library, these changes will be lost."
        onClose={() => setOpenCloseWithoutSavingDialog(false)}
        maxWidth="sm"
        cancelButtonText="Cancel"
        cancelButtonFireEvent={() => setOpenCloseWithoutSavingDialog(false)}
        cancelButtonColor="white"
        confirmationButtonColor="blue"
        confirmationButtonText="Continue"
        confirmationButtonFireEvent={() => {
          setOpenCloseWithoutSavingDialog(false);
          setIsEditing(false);
          fireEvent?.("stopLayoutEdit");
          setTabs(inputTabs.length > 0 ? inputTabs : [defaultTab]);
        }}
      ></PMPopUp>
    </>
  );
};
